import { getUsersPreferencesWaterCharacteristicsQueryKey } from "@imi/api-client/index.js";
import { UndoIcon } from "@imi/icons/UndoIcon.js";
import { Button } from "@imi/ui/components/Button/Button.js";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogClose,
  DialogCloseButton,
  DialogHeader,
} from "@imi/ui/components/Dialog/Dialog.js";
import { Heading } from "@imi/ui/components/Heading/Heading.js";
import { Switch } from "@imi/ui/components/Switch/Switch.js";
import { SwitchLabel } from "@imi/ui/components/Switch/SwitchLabel.js";
import { selectSubmitOnEnter } from "@src/shared/helpers/form";
import { useQueryClient } from "@tanstack/react-query";
import { Suspense } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useTranslation } from "react-i18next";
import { Form } from "react-router";
import { useRouteDialogProps } from "../../hooks/useRouteDialogProps";
import { DialogErrorFallback } from "./components/DialogErrorFallback";
import { DialogLoadingFallback } from "./components/DialogLoadingFallback";
import { ExistingAdditivesForm } from "./components/ExistingAdditivesForm";
import { WaterInformationForm } from "./components/WaterInformationForm";
import { WaterPhase } from "./components/WaterPhase";
import { useWaterCharacteristicsForm } from "./hooks/useWaterCharacteristicsForm";
import styles from "./SettingsWaterCharacteristicsDialog.module.css";

export function SettingsWaterCharacteristicsDialog() {
  const { t } = useTranslation("settings", {
    keyPrefix: "technicalPreferences.waterCharacteristics",
  });
  const routeDialogProps = useRouteDialogProps();

  return (
    <Dialog
      {...routeDialogProps}
      className={styles.SettingsWaterCharacteristics}
    >
      <ErrorBoundary FallbackComponent={DialogErrorFallback}>
        <Suspense fallback={<DialogLoadingFallback />}>
          <SettingsWaterCharacteristicsDialogContent />
        </Suspense>
      </ErrorBoundary>
      <DialogCloseButton>{t("actions.close")}</DialogCloseButton>
    </Dialog>
  );
}

function SettingsWaterCharacteristicsDialogContent() {
  const { t } = useTranslation("settings", {
    keyPrefix: "technicalPreferences.waterCharacteristics",
  });
  const routeDialogProps = useRouteDialogProps();
  const queryClient = useQueryClient();

  const { defaultValue, definitions, form, submitting } =
    useWaterCharacteristicsForm({
      onSubmit: () => {
        queryClient.invalidateQueries({
          queryKey: getUsersPreferencesWaterCharacteristicsQueryKey(),
        });
        routeDialogProps.onOpenChange(false);
      },
    });

  return (
    <>
      <DialogHeader>
        <Heading as="h3" subTitle={t("subTitle")}>
          {t("title")}
        </Heading>
        <WaterPhase form={form} />
      </DialogHeader>
      <DialogBody className={styles.DialogBody}>
        <Form
          className={styles.WrapExistingAdditivesForm}
          id={form.formId}
          onKeyDown={(e) => selectSubmitOnEnter(e, submitting)}
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit(e);
          }}
        >
          <fieldset>
            <form.AppField name="mode">
              {(field) => (
                <SwitchLabel label={t("manualEntry")}>
                  <Switch
                    checked={field.state.value === "manual"}
                    onChange={() => {
                      if (field.state.value === "manual") {
                        field.handleChange("predefined");
                      } else {
                        field.handleChange("manual");
                      }
                    }}
                  />
                </SwitchLabel>
              )}
            </form.AppField>
          </fieldset>

          <form.Subscribe selector={(state) => state.values.mode}>
            {(mode) =>
              mode === "manual" ? (
                <WaterInformationForm definitions={definitions} form={form} />
              ) : (
                <ExistingAdditivesForm definitions={definitions} form={form} />
              )
            }
          </form.Subscribe>
        </Form>
      </DialogBody>

      <DialogActions>
        <Button
          className={styles.ResetButton}
          disabled={submitting}
          onClick={() => {
            form.reset(defaultValue);
          }}
          variant="outline"
        >
          <UndoIcon />
          {t("actions.reset")}
        </Button>
        <DialogClose disabled={submitting} variant="outline">
          {t("actions.cancel")}
        </DialogClose>
        <Button form={form.formId} loading={submitting} type="submit">
          {t("actions.confirm")}
        </Button>
      </DialogActions>
    </>
  );
}
